import { useEffect, useRef } from 'react';
import styled from 'styled-components';

const Canvas = styled.canvas`
    display: block;
    width: 100%;
    height: 100%;
    cursor: default;
`;

/**
 * TimeRuler — draws time tick marks on a canvas.
 *
 * Props:
 *   duration      — original video duration in seconds
 *   zoomLevel     — pixels per second
 *   scrollLeft    — horizontal scroll offset of the timeline
 *   visibleWidth  — pixel width of the visible area
 *   cuts          — array of { startTime, endTime } deleted segments
 */
export default function TimeRuler({ duration, zoomLevel, scrollLeft, visibleWidth, labelWidth = 0 }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const dpr = window.devicePixelRatio || 1;
        const W = canvas.offsetWidth;
        const H = canvas.offsetHeight;
        canvas.width = W * dpr;
        canvas.height = H * dpr;

        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);

        // Background
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, W, H);

        if (!duration || zoomLevel <= 0) return;

        const totalWidth = duration * zoomLevel;
        const startSec = scrollLeft / zoomLevel;
        const endSec = (scrollLeft + visibleWidth) / zoomLevel;

        // Choose tick interval based on zoom
        const minTickPx = 60;
        const rawInterval = minTickPx / zoomLevel;
        const niceIntervals = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
        const tickInterval = niceIntervals.find((iv) => iv >= rawInterval) || 600;
        const subTickInterval = tickInterval / 5;

        ctx.font = '10px monospace';
        ctx.textAlign = 'center';

        // Draw sub-ticks
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 1;
        const firstSubTick = Math.floor(startSec / subTickInterval) * subTickInterval;
        for (let t = firstSubTick; t <= endSec + subTickInterval; t += subTickInterval) {
            const x = labelWidth + t * zoomLevel - scrollLeft;
            if (x < 0 || x > W) continue;
            ctx.beginPath();
            ctx.moveTo(x, H);
            ctx.lineTo(x, H - 5);
            ctx.stroke();
        }

        // Draw major ticks + labels
        ctx.strokeStyle = '#666';
        ctx.fillStyle = '#aaa';
        ctx.lineWidth = 1;
        const firstTick = Math.floor(startSec / tickInterval) * tickInterval;
        for (let t = firstTick; t <= endSec + tickInterval; t += tickInterval) {
            const x = labelWidth + t * zoomLevel - scrollLeft;
            if (x < -80 || x > W + 80) continue;
            ctx.beginPath();
            ctx.moveTo(x, H);
            ctx.lineTo(x, H - 12);
            ctx.stroke();

            // Format label
            const label = formatRulerTime(t);
            ctx.fillText(label, x, H - 15);
        }

        // Bottom border line
        ctx.strokeStyle = '#444';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, H - 1);
        ctx.lineTo(W, H - 1);
        ctx.stroke();
    }, [duration, zoomLevel, scrollLeft, visibleWidth]);

    return <Canvas ref={canvasRef} />;
}

function formatRulerTime(seconds) {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}
